import { useRef, useEffect, Fragment } from "react";
import { Stack, Button, useTheme, Alert } from "@mui/material";
import { FormPropertyProps } from "../../../FormProperty";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { MultiElementsGroup } from "./MultiElementsGroup";
import { usePropertyContext } from "../../../context/usePropertyContext";
import { NoElementsForMulti } from "../NoElements";
import { useMultivalueProperty } from "../../../context/hooks/useMultivalueProperty";

type MultiLayer = { level?: number };
export type GroupMultivaluedProps = FormPropertyProps & MultiLayer;

export const GroupMultivalued = (props: GroupMultivaluedProps) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { getValidationError, canEdit } = usePropertyContext();

    const { property, level = 0, dimensionValue } = props;

    const { elementsGroup, addElementsGroup, deleteElementsGroup, markAsNotApplicable } = useMultivalueProperty({
        property,
        dimensionValue,
    });

    const alertRef = useRef<HTMLDivElement>(null);
    const validationState = getValidationError(property.id, props.position ?? 0);

    useEffect(() => {
        if (validationState.validationMessages && validationState.validationMessages.length > 0 && alertRef.current) {
            alertRef.current.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [validationState.validationMessages]);

    const existingElements = elementsGroup.length;
    if (existingElements === 0) {
        return (
            <NoElementsForMulti property={property} onAdd={addElementsGroup} onNotApplicable={markAsNotApplicable} />
        );
    }

    return (
        <Stack direction="column" alignItems="flex-start" gap={theme.spacing(2)}>
            {validationState.hasValidationMessages && (
                <div ref={alertRef} style={{ width: "100%" }}>
                    {validationState.validationMessages?.map((message, index) => {
                        const isError = message.severity === "error";
                        const color = isError ? theme.custom.red : theme.custom.orange;
                        const backgroundColor = isError ? theme.custom.red[50] : theme.custom.orange[50];
                        const baseStyles = {
                            width: "100%",
                            backgroundColor: backgroundColor,
                            borderLeft: `4px solid ${color[500]}`,
                            color: color[900],
                            "& .MuiAlert-icon": {
                                color: color[500],
                            },
                        };

                        return (
                            <Alert
                                key={index}
                                severity={message.severity === "error" ? "error" : "warning"}
                                sx={baseStyles}
                            >
                                {message.message}
                            </Alert>
                        );
                    })}
                </div>
            )}
            <Stack gap={theme.spacing(3)} width={"100%"}>
                {elementsGroup.map((elementGroup, idx) => {
                    const itemNb = idx + 1;
                    return (
                        <Fragment key={`multivalue-group-${elementGroup.position}`}>
                            <MultiElementsGroup
                                elementGroup={elementGroup}
                                itemNb={itemNb}
                                deleteElement={deleteElementsGroup}
                                level={level}
                                showSubscore={props.showSubscore}
                            />
                        </Fragment>
                    );
                })}
            </Stack>
            {canEdit && (
                <Button variant="text" color="primary" startIcon={<AddIcon />} onClick={addElementsGroup}>
                    {t("ajouter_un_element")}
                </Button>
            )}
        </Stack>
    );
};
